import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../api';
import { FiChevronLeft, FiChevronRight, FiArrowRight } from 'react-icons/fi';
import { getImageUrl } from '../utils/imageUtils';

// No hardcoded fallback slides. The slider is fully driven by the admin.

const HeroCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const { data } = await API.get('/api/banners');
        // Filter for active hero banners
        const heroBanners = data.banners.filter(b => b.isActive && b.position === 'hero');
        setBanners(heroBanners);
      } catch (err) {
        console.error('Failed to fetch hero banners', err);
        setBanners([]);
      } finally {
        setLoaded(true);
      }
    };
    fetchBanners();
  }, []);

  // Auto-slide logic
  useEffect(() => {
    if (banners.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setActiveIndex((current) => (current + 1) % banners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [banners.length, isPaused]);

  const handleImageError = (e) => {
    e.target.src = 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=1200&q=80&auto=format&fit=crop';
  };

  if (!loaded) {
    return (
      <div className="relative h-[85vh] md:h-[90vh] w-full overflow-hidden bg-[#1A1A2E]">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#C9A96E]/20 border-t-[#C9A96E] rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[#C9A96E] text-xs font-inter tracking-[0.4em] uppercase opacity-60">
              Loading Elegance
            </p>
          </div>
        </div>
      </div>
    );
  }

  const nextSlide = () => setActiveIndex((current) => (current + 1) % banners.length);
  const prevSlide = () => setActiveIndex((current) => (current === 0 ? banners.length - 1 : current - 1));

  if (banners.length === 0 && loaded) {
    return null; // Render nothing if no hero banners are configured
  }

  return (
    <div 
      className="relative w-full h-[85vh] min-h-[600px] overflow-hidden group"
      style={{ background: 'linear-gradient(135deg, #1A1A2E 0%, #2D2B3D 50%, #1A1A2E 100%)' }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Slides */}
      {banners.map((banner, index) => (
        <div 
          key={banner._id}
          className={`absolute inset-0 transition-all duration-1000 ease-in-out flex items-center ${
            index === activeIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Background Image Container — showcases the full banner image */}
          <div className="absolute inset-0 overflow-hidden">
            <div 
              className={`absolute inset-0 bg-cover bg-center transition-transform duration-[10000ms] ease-out ${
                index === activeIndex ? 'scale-110' : 'scale-100'
              }`}
              style={{
                backgroundImage: `url(${getImageUrl(banner.imageUrl, 'hero')})`,
              }}
            />
            {/* Fallback if backgroundImage fails (though handled by getImageUrl usually) */}
            <img 
              src={getImageUrl(banner.imageUrl, 'hero')} 
              alt={banner.title || "Poshe Styles Premium Banner"} 
              className="hidden" 
              onError={handleImageError} 
            />
          </div>
          
          {/* Refined Overlays — light enough to see the image, dark enough for text */}
          {/* Gentle warm tint for brand consistency */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(26,26,46,0.15) 0%, rgba(201,169,110,0.05) 100%)' }} />
          
          {/* Left-side gradient — only where text lives, fades to fully transparent */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(26,26,46,0.75) 0%, rgba(26,26,46,0.45) 30%, rgba(26,26,46,0.10) 55%, transparent 70%)' }} />
          
          {/* Bottom gentle warmth for page transition */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(26,26,46,0.30) 0%, transparent 35%)' }} />
          
          {/* Content */}
          <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 w-full pt-12 md:pt-0">
            <div 
              className="max-w-2xl transition-all duration-1000 delay-300"
              style={{ 
                transform: index === activeIndex ? 'translateY(0)' : 'translateY(40px)',
                opacity: index === activeIndex ? 1 : 0,
              }}
            >
              {/* Premium Label */}
              {banner.badgeText && (
                <div className="flex items-center gap-4 mb-8 overflow-hidden">
                  <div className={`h-[1px] transition-all duration-1000 delay-500 ${index === activeIndex ? 'w-16' : 'w-0'}`} style={{ background: 'linear-gradient(to right, #C9A96E, #E8D5B0)' }} />
                  <span className="font-inter text-[10px] md:text-xs tracking-[0.5em] uppercase font-bold" style={{ color: '#E8D5B0', textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>
                    {banner.badgeText}
                  </span>
                </div>
              )}
              
              <h2 
                className="font-playfair text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium text-white leading-[1.1] mb-6"
                style={{ 
                  textShadow: '0 2px 20px rgba(0,0,0,0.5), 0 4px 40px rgba(0,0,0,0.3)',
                  letterSpacing: '-0.01em'
                }}
              >
                {banner.title}
                {banner.accentText && <span className="block italic mt-2" style={{ color: '#E8D5B0', textShadow: '0 2px 16px rgba(201,169,110,0.4)' }}>{banner.accentText}</span>}
              </h2>
              
              {banner.paragraph && (
                <p className="text-base md:text-xl mb-12 font-inter font-light leading-relaxed max-w-lg" style={{ color: 'rgba(255,255,255,0.90)', textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}>
                  {banner.paragraph}
                </p>
              )}
              
              <div className="flex flex-col sm:flex-row gap-5">
                {banner.primaryBtnText && (
                  <Link 
                    to={banner.primaryBtnLink || '/products'} 
                    className="group relative overflow-hidden text-white px-10 py-4 rounded-sm font-semibold text-xs tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3"
                    style={{ 
                      background: 'linear-gradient(135deg, #C9A96E 0%, #D4B87A 50%, #C9A96E 100%)',
                      boxShadow: '0 4px 20px rgba(201,169,110,0.35), 0 0 40px rgba(201,169,110,0.10)'
                    }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 8px 35px rgba(201,169,110,0.50), 0 0 60px rgba(201,169,110,0.15)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(201,169,110,0.35), 0 0 40px rgba(201,169,110,0.10)'}
                  >
                    <span className="relative z-10">{banner.primaryBtnText}</span>
                    <FiArrowRight className="relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
                    <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500" />
                  </Link>
                )}
                
                {banner.secondaryBtnText && (
                  <Link 
                    to={banner.secondaryBtnLink || '/products?sort=popular'} 
                    className="group px-10 py-4 rounded-sm font-semibold text-xs tracking-[0.2em] uppercase transition-all duration-300 flex items-center justify-center backdrop-blur-sm"
                    style={{ 
                      border: '1px solid rgba(232,213,176,0.35)',
                      color: '#F5EFE4',
                      textShadow: '0 1px 4px rgba(0,0,0,0.3)'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(232,213,176,0.7)'; e.currentTarget.style.background = 'rgba(201,169,110,0.10)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(232,213,176,0.35)'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    {banner.secondaryBtnText}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Controls */}
      {banners.length > 1 && (
        <>
          <button 
            onClick={prevSlide} 
            className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-14 h-14 
                       backdrop-blur-md rounded-full 
                       flex items-center justify-center text-white transition-all duration-500
                       opacity-0 group-hover:opacity-100 -translate-x-4 group-hover:translate-x-0"
            style={{ background: 'rgba(201,169,110,0.15)', border: '1px solid rgba(201,169,110,0.25)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(201,169,110,0.80)'; e.currentTarget.style.borderColor = 'rgba(201,169,110,0.9)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(201,169,110,0.15)'; e.currentTarget.style.borderColor = 'rgba(201,169,110,0.25)'; }}
            aria-label="Previous slide"
          >
            <FiChevronLeft size={24} />
          </button>
          
          <button 
            onClick={nextSlide} 
            className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-14 h-14 
                       backdrop-blur-md rounded-full 
                       flex items-center justify-center text-white transition-all duration-500
                       opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0"
            style={{ background: 'rgba(201,169,110,0.15)', border: '1px solid rgba(201,169,110,0.25)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(201,169,110,0.80)'; e.currentTarget.style.borderColor = 'rgba(201,169,110,0.9)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(201,169,110,0.15)'; e.currentTarget.style.borderColor = 'rgba(201,169,110,0.25)'; }}
            aria-label="Next slide"
          >
            <FiChevronRight size={24} />
          </button>

          {/* Progress Indicators with gold accent */}
          <div className="absolute bottom-12 right-12 z-30 flex items-center gap-6">
             <div className="font-inter text-[10px] tracking-[0.3em] font-medium" style={{ color: 'rgba(232,213,176,0.60)' }}>
                0{activeIndex + 1} / 0{banners.length}
             </div>
             <div className="flex gap-2">
                {banners.map((_, index) => (
                  <button 
                    key={index} 
                    onClick={() => setActiveIndex(index)}
                    className="transition-all duration-700"
                    style={{
                      height: '2px',
                      width: index === activeIndex ? '48px' : '16px',
                      background: index === activeIndex 
                        ? 'linear-gradient(to right, #C9A96E, #E8D5B0)' 
                        : 'rgba(255,255,255,0.25)',
                      boxShadow: index === activeIndex ? '0 0 12px rgba(201,169,110,0.40)' : 'none'
                    }}
                  />
                ))}
             </div>
          </div>
        </>
      )}

      {/* Decorative gold accent line at bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none" style={{ height: '2px', background: 'linear-gradient(to right, transparent 10%, rgba(201,169,110,0.30) 30%, rgba(201,169,110,0.50) 50%, rgba(201,169,110,0.30) 70%, transparent 90%)' }} />

      {/* Bottom edge fade — smooth transition into page content */}
      <div className="absolute bottom-0 left-0 right-0 h-24 z-20 pointer-events-none" style={{ background: 'linear-gradient(to top, #FAFAF8 0%, rgba(250,250,248,0.60) 40%, transparent 100%)' }} />
    </div>
  );
};

export default HeroCarousel;
